import { BookingNumber, getBooleanLabel, RedeliveryNumber, UnitNumber } from 'components/tables/utils';
import { ActiveUnit, Role } from 'types';
import { T_DataTableColumn } from 'types/data-table';

interface UnitColumnType {
  billOfLading: T_DataTableColumn<ActiveUnit, 'text'>;
  booking: T_DataTableColumn<ActiveUnit, 'booking'>;
  carbCompliant: T_DataTableColumn<ActiveUnit, 'boolean'>;
  createdBy: T_DataTableColumn<ActiveUnit, 'text'>;
  customer: T_DataTableColumn<ActiveUnit, 'customer'>;
  customerName: T_DataTableColumn<ActiveUnit, 'text'>;
  customerFullName: T_DataTableColumn<ActiveUnit, 'text'>;
  customerCode: T_DataTableColumn<ActiveUnit, 'text'>;
  customerLocation: T_DataTableColumn<ActiveUnit, 'text'>;
  deliveredDate: T_DataTableColumn<ActiveUnit, 'dateTime'>;
  equipmentType: T_DataTableColumn<ActiveUnit, 'equipmentType'>;
  flatbed: T_DataTableColumn<ActiveUnit, 'boolean'>;
  fmcsaDate: T_DataTableColumn<ActiveUnit, 'date'>;
  fuelLevel: T_DataTableColumn<ActiveUnit, 'number'>;
  hazard: T_DataTableColumn<ActiveUnit, 'boolean'>;
  holdNotes: T_DataTableColumn<ActiveUnit, 'text'>;
  humidity: T_DataTableColumn<ActiveUnit, 'number'>;
  id: T_DataTableColumn<ActiveUnit, 'number'>;
  licensePlate: T_DataTableColumn<ActiveUnit, 'text'>;
  loaded: T_DataTableColumn<ActiveUnit, 'boolean'>;
  location: T_DataTableColumn<ActiveUnit, 'location'>;
  licensePlateExpirationDate: T_DataTableColumn<ActiveUnit, 'date'>;
  manufacturedDate: T_DataTableColumn<ActiveUnit, 'date'>;
  notes: T_DataTableColumn<ActiveUnit, 'text'>;
  onHold: T_DataTableColumn<ActiveUnit, 'boolean'>;
  ownerChassis: T_DataTableColumn<ActiveUnit, 'boolean'>;
  receivedDate: T_DataTableColumn<ActiveUnit, 'dateTime'>;
  redelivery: T_DataTableColumn<ActiveUnit, 'redelivery'>;
  seal0: T_DataTableColumn<ActiveUnit, 'text'>;
  seal1: T_DataTableColumn<ActiveUnit, 'text'>;
  seal2: T_DataTableColumn<ActiveUnit, 'text'>;
  sizeType: T_DataTableColumn<ActiveUnit, 'sizeType'>;
  statusReason: T_DataTableColumn<ActiveUnit, 'text'>;
  temperature: T_DataTableColumn<ActiveUnit, 'number'>;
  unitGrade: T_DataTableColumn<ActiveUnit, 'text'>;
  unitNumber: T_DataTableColumn<ActiveUnit, 'text'>;
  unitStatus: T_DataTableColumn<ActiveUnit, 'unitStatus'>;
  ventilation: T_DataTableColumn<ActiveUnit, 'text'>;
  weight: T_DataTableColumn<ActiveUnit, 'number'>;
  daysInYard: T_DataTableColumn<ActiveUnit, 'number'>;
}

type UnitDataTableColumn = { [P in keyof UnitColumnType]: UnitColumnType[P] }[keyof UnitColumnType];

export const unitDataColumns: UnitDataTableColumn[] = [
  /* common columns */
  { width: 80, category: 'common', field: 'id', label: 'RECORD', type: 'number', skipQuickSearch: true },
  {
    width: 80,
    category: 'common',
    field: 'unitNumber',
    label: 'UNIT NUMBER',
    type: 'text',
    renderCell: (_, row) => (
      <UnitNumber unit={row} ADMIN BILLING_ADMIN SENIOR_BOOKING BOOKING GATE_BOOKING ADMIN_CUSTOMER CUSTOMER EXTERNAL />
    )
  },
  { width: 80, category: 'common', field: 'equipmentType', label: 'EQUIPMENT TYPE', type: 'equipmentType' },
  { width: 80, category: 'common', field: 'sizeType', label: 'SIZE TYPE', type: 'sizeType' },
  {
    width: 80,
    category: 'common',
    field: 'redelivery',
    label: 'REDELIVERY',
    type: 'redelivery',
    getValueLabel: r => r?.redeliveryNumber,
    renderCell: r => <RedeliveryNumber redelivery={r} ADMIN BILLING_ADMIN SENIOR_BOOKING BOOKING CUSTOMER EXTERNAL />
  },
  {
    width: 80,
    category: 'common',
    field: 'booking',
    label: 'BOOKING',
    type: 'booking',
    getValueLabel: value => value?.bookingNumber,
    renderCell: value => <BookingNumber booking={value} ADMIN BILLING_ADMIN SENIOR_BOOKING BOOKING CUSTOMER EXTERNAL />
  },
  {
    width: 80,
    category: 'common',
    field: 'customer',
    label: 'ACCOUNT',
    type: 'customer',
    getValueLabel: customer => customer?.name
  },
  {
    width: 80,
    category: 'common',
    field: 'customerCode',
    label: 'ACCOUNT CODE',
    type: 'text'
  },
  {
    width: 80,
    category: 'common',
    field: 'customerFullName',
    label: 'ACCOUNT FULL NAME',
    type: 'text'
  },
  {
    width: 80,
    category: 'common',
    field: 'customerLocation',
    label: 'ACCOUNT LOCATION',
    type: 'text'
  },
  {
    width: 80,
    category: 'common',
    field: 'customerName',
    label: 'ACCOUNT NAME',
    type: 'text'
  },
  {
    width: 80,
    category: 'common',
    field: 'location',
    label: 'LOCATION',
    type: 'location',
    getValueLabel: location => location?.name
  },
  {
    width: 80,
    category: 'common',
    field: 'receivedDate',
    label: 'GATE IN',
    type: 'dateTime',
    dateFormat: 'M/D/YY LT'
  },
  {
    width: 80,
    category: 'common',
    field: 'deliveredDate',
    label: 'GATE OUT',
    type: 'dateTime',
    dateFormat: 'M/D/YY LT'
  },
  {
    width: 80,
    category: 'common',
    field: 'manufacturedDate',
    label: 'MANUFACTURED',
    type: 'date',
    dateFormat: 'M/D/YY'
  },
  {
    width: 80,
    category: 'common',
    field: 'createdBy',
    label: 'GATE USER',
    allowedRoles: [Role.ADMIN, Role.BILLING_ADMIN, Role.BOOKING]
  },
  { width: 80, category: 'common', field: 'unitStatus', label: 'UNIT STATUS', type: 'unitStatus' },
  { width: 80, category: 'common', field: 'statusReason', label: 'STATUS NOTES' },
  { width: 80, category: 'common', field: 'unitGrade', label: 'UNIT GRADE' },
  {
    width: 80,
    category: 'common',
    field: 'onHold',
    label: 'ON HOLD',
    type: 'boolean',
    getValueLabel: getBooleanLabel,
    skipQuickSearch: true
  },
  { width: 80, category: 'common', field: 'holdNotes', label: 'HOLD NOTES' },
  { width: 80, category: 'common', field: 'notes', label: 'NOTES' },
  { width: 80, category: 'common', field: 'daysInYard', label: 'DAYS IN YARD', type: 'number', skipQuickSearch: true },

  /* container-specific columns */
  {
    width: 80,
    category: 'container',
    field: 'hazard',
    label: 'HAZARD',
    type: 'boolean',
    getValueLabel: getBooleanLabel,
    skipQuickSearch: true
  },
  {
    width: 80,
    category: 'container',
    field: 'loaded',
    label: 'LOADED',
    type: 'boolean',
    getValueLabel: getBooleanLabel,
    skipQuickSearch: true
  },
  { width: 80, category: 'container', field: 'seal0', label: 'SEAL #1' },
  { width: 80, category: 'container', field: 'seal1', label: 'SEAL #2' },
  { width: 80, category: 'container', field: 'seal2', label: 'SEAL #3' },
  { width: 80, category: 'container', field: 'billOfLading', label: 'LADING' },
  { width: 80, category: 'container', field: 'weight', label: 'WEIGHT', type: 'number', skipQuickSearch: true },

  /* chassis-specific columns */
  {
    width: 80,
    category: 'chassis',
    field: 'flatbed',
    label: 'FLATBED',
    type: 'boolean',
    getValueLabel: getBooleanLabel,
    skipQuickSearch: true
  },
  {
    width: 80,
    category: 'chassis',
    field: 'ownerChassis',
    label: 'OWNER',
    type: 'boolean',
    getValueLabel: getBooleanLabel,
    skipQuickSearch: true
  },
  { width: 80, category: 'chassis', field: 'licensePlate', label: 'LICENSE PLATE' },
  {
    width: 80,
    category: 'chassis',
    field: 'fmcsaDate',
    label: 'FMCSA',
    type: 'date',
    dateFormat: 'MM/YYYY'
  },
  {
    width: 80,
    category: 'chassis',
    field: 'licensePlateExpirationDate',
    label: 'LP EXP',
    type: 'date',
    dateFormat: 'MM/YYYY'
  },

  /* genset-specfic columns */
  { width: 80, category: 'genset', field: 'humidity', label: 'HUMIDITY', type: 'number', skipQuickSearch: true },
  { width: 80, category: 'genset', field: 'temperature', label: 'TEMPERATURE', type: 'number', skipQuickSearch: true },
  { width: 80, category: 'genset', field: 'ventilation', label: 'VENTILATION' },
  { width: 80, category: 'genset', field: 'fuelLevel', label: 'FUEL', type: 'number', skipQuickSearch: true },
  {
    width: 80,
    category: 'genset',
    field: 'carbCompliant',
    label: 'CARB',
    type: 'boolean',
    getValueLabel: getBooleanLabel,
    skipQuickSearch: true
  }
];

export const defaultUnitColumns: Array<keyof UnitColumnType> = [
  'unitNumber',
  'equipmentType',
  'sizeType',
  'unitStatus',
  'customer',
  'booking',
  'receivedDate',
  'deliveredDate'
];

export default unitDataColumns;
